import { ScheduledDeliverable } from '@/models/ScheduleDeliverable';
import { ScheduledDeliverablesAllTypes } from '@/models/ScheduledDeliverables';

import { ScheduleDeliverableLog } from 'models/ScheduleDeliverableLog';
import { ActionType, getType } from 'typesafe-actions';

import { MemoSubToggleModes } from '../../components/DeliverableMemos';
import { MemosState } from '../../components/DeliverableMemos/types';
import {
  DeliverableReportMapping,
  DeliverableReportModes,
} from '../../components/DeliverableReports';
import { ReportsState } from '../../components/DeliverableReports/types';

import { TransactionItemProps } from '../components/DeliverablesList/components/Actions/CreateEditModal/components/TransactionsTable/types';
import {
  ReportDownloadItem,
  ScheduledDeliverablesTestReport,
  ScheduledDeliverablesTestReports,
} from '../types';

import * as scheduledDeliverablesActions from './actions';

type ScheduledDeliverablesActionType = ActionType<
  typeof scheduledDeliverablesActions
>;

export type ScheduledDeliverablesState = {
  scheduledDeliverablesList: {
    data: Array<ScheduledDeliverablesAllTypes>;
    loading: boolean;
  };
  scheduledDeliverables: {
    [key: string]: {
      data: ScheduledDeliverable;
      loading: boolean;
      activityLogs: {
        data: ScheduleDeliverableLog[];
        loading: boolean;
      };
      emails: {
        data: string[];
        loading: boolean;
      };
      reportListDownload: {
        data: ReportDownloadItem[];
        loading: false;
      };
      newTrades: {
        data: number[];
        loading: boolean;
      };
      memosEnabled: boolean;
      scheduledReportToggles: ReportsState;
      scheduledMemoToggles: MemosState;
      reportLoading: boolean;
    };
  };
  transactions: {
    data: TransactionItemProps[];
    loading: boolean;
  };
  scheduledDeliverablesTransactions: {
    [key: string]: string[];
  };
  markedAsCompletedLoading: boolean;
  deleteLoading: boolean;
  cloneLoading: boolean;
  emailSendLoading: boolean;
  deleteReportLoading: boolean;
  createUpdateLoading: boolean;
  testReports: {
    data: ScheduledDeliverablesTestReports[];
    loading: boolean;
  };
  testReport: {
    data: ScheduledDeliverablesTestReport | null;
    loading: boolean;
  };
  rerunValidationsLoading: boolean;
};

const initialState: ScheduledDeliverablesState = {
  scheduledDeliverablesList: {
    data: [],
    loading: false,
  },
  scheduledDeliverables: {},
  transactions: {
    data: [],
    loading: false,
  },
  scheduledDeliverablesTransactions: {},
  markedAsCompletedLoading: false,
  deleteLoading: false,
  cloneLoading: false,
  emailSendLoading: false,
  deleteReportLoading: false,
  createUpdateLoading: false,
  testReports: {
    data: [],
    loading: false,
  },
  testReport: {
    data: null,
    loading: false,
  },
  rerunValidationsLoading: false,
};

const scheduledDeliverablesReducer = (
  state: ScheduledDeliverablesState = initialState,
  action: ScheduledDeliverablesActionType,
): ScheduledDeliverablesState => {
  switch (action.type) {
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesList.request,
    ):
      return {
        ...state,
        scheduledDeliverablesList: {
          ...state.scheduledDeliverablesList,
          loading: true,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesList.success,
    ): {
      return {
        ...state,
        scheduledDeliverablesList: {
          ...state.scheduledDeliverablesList,
          data: action.payload.data,
          loading: false,
        },
      };
    }
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesList.failure,
    ):
      return {
        ...state,
        scheduledDeliverablesList: {
          ...state.scheduledDeliverablesList,
          loading: false,
        },
      };

    case getType(scheduledDeliverablesActions.getScheduledDeliverable.request):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            loading: true,
          },
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverable.success,
    ): {
      const data = action.payload.data;
      const initialReportToggles = {
        [DeliverableReportModes.Statement]: false,
        [DeliverableReportModes.Valuation]: false,
        [DeliverableReportModes.JournalEntries]: false,
        [DeliverableReportModes.Regression]: false,
        [DeliverableReportModes.JournalEntryAtMaturity]: false,
      };

      if (data.report_types && data.report_types.length > 0) {
        data.report_types.forEach((reportType: number) => {
          switch (reportType) {
            case DeliverableReportMapping.Statement:
              initialReportToggles[DeliverableReportModes.Statement] = true;
              break;
            case DeliverableReportMapping.Valuation:
              initialReportToggles[DeliverableReportModes.Valuation] = true;
              break;
            case DeliverableReportMapping.JournalEntries:
              initialReportToggles[DeliverableReportModes.JournalEntries] =
                true;
              break;
            case DeliverableReportMapping.Regression:
              initialReportToggles[DeliverableReportModes.Regression] = true;
              break;
            case DeliverableReportMapping.JournalEntryAtMaturity:
              initialReportToggles[
                DeliverableReportModes.JournalEntryAtMaturity
              ] = true;
              break;
          }
        });
      }

      const scheduledMemoToggles = {
        [MemoSubToggleModes.Memo]: data?.memos?.hdm ?? true,
        [MemoSubToggleModes.ActualsTermsheet]: data?.memos?.termsheet ?? true,
        [MemoSubToggleModes.HypotheticalsTermsheet]:
          data?.memos?.hyp_termsheet ?? true,
        [MemoSubToggleModes.AggregateNotional]:
          data?.memos?.agg_notional ?? true,
        [MemoSubToggleModes.Effectiveness]: data?.memos?.effectiveness ?? true,
      };

      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.data.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.data.schedule_id],
            loading: false,
            data: action.payload.data,
            memosEnabled: !(data.report_types && data.report_types.length > 0),
            scheduledReportToggles: initialReportToggles,
            scheduledMemoToggles: scheduledMemoToggles,
          },
        },
      };
    }
    case getType(scheduledDeliverablesActions.getScheduledDeliverable.failure):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            loading: false,
          },
        },
      };

    case getType(scheduledDeliverablesActions.runDeliverableReport.request):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            reportLoading: true,
          },
        },
      };
    case getType(scheduledDeliverablesActions.runDeliverableReport.success):
    case getType(scheduledDeliverablesActions.runDeliverableReport.failure): {
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            reportLoading: false,
          },
        },
      };
    }

    case getType(scheduledDeliverablesActions.getActivityLogs.request):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            activityLogs: {
              loading: true,
              data: [],
            },
          },
        },
      };
    case getType(scheduledDeliverablesActions.getActivityLogs.success): {
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            activityLogs: {
              loading: false,
              data: action.payload.data,
            },
          },
        },
      };
    }
    case getType(scheduledDeliverablesActions.getActivityLogs.failure):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            activityLogs: {
              loading: false,
              data: [],
            },
          },
        },
      };

    case getType(scheduledDeliverablesActions.getClientEmails.request):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            emails: {
              loading: true,
              data: [],
            },
          },
        },
      };
    case getType(scheduledDeliverablesActions.getClientEmails.success): {
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            emails: {
              loading: false,
              data: action.payload.data,
            },
          },
        },
      };
    }
    case getType(scheduledDeliverablesActions.getClientEmails.failure):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            emails: {
              loading: false,
              data: [],
            },
          },
        },
      };

    case getType(scheduledDeliverablesActions.getReportListDownload.request):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            reportListDownload: {
              loading: true,
              data: [],
            },
          },
        },
      };
    case getType(scheduledDeliverablesActions.getReportListDownload.success): {
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            reportListDownload: {
              loading: false,
              data: action.payload.data,
            },
          },
        },
      };
    }
    case getType(scheduledDeliverablesActions.getReportListDownload.failure):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            reportListDownload: {
              loading: false,
              data: [],
            },
          },
        },
      };

    case getType(scheduledDeliverablesActions.getNewTrades.request):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            newTrades: {
              loading: true,
              data: [],
            },
          },
        },
      };
    case getType(scheduledDeliverablesActions.getNewTrades.success): {
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            newTrades: {
              loading: false,
              data: action.payload.data,
            },
          },
        },
      };
    }
    case getType(scheduledDeliverablesActions.getNewTrades.failure):
      return {
        ...state,
        scheduledDeliverables: {
          ...state.scheduledDeliverables,
          [action.payload.schedule_id]: {
            ...state.scheduledDeliverables[action.payload.schedule_id],
            newTrades: {
              loading: false,
              data: [],
            },
          },
        },
      };

    case getType(scheduledDeliverablesActions.setMarkAsCompleted.request):
      return {
        ...state,
        markedAsCompletedLoading: true,
      };
    case getType(scheduledDeliverablesActions.setMarkAsCompleted.success):
    case getType(scheduledDeliverablesActions.setMarkAsCompleted.failure): {
      return {
        ...state,
        markedAsCompletedLoading: false,
      };
    }

    case getType(
      scheduledDeliverablesActions.createUpdateScheduledDeliverable.request,
    ):
      return {
        ...state,
        createUpdateLoading: true,
      };
    case getType(
      scheduledDeliverablesActions.createUpdateScheduledDeliverable.success,
    ):
    case getType(
      scheduledDeliverablesActions.createUpdateScheduledDeliverable.failure,
    ): {
      return {
        ...state,
        createUpdateLoading: false,
      };
    }

    case getType(scheduledDeliverablesActions.deleteDeliverable.request):
      return {
        ...state,
        deleteLoading: true,
      };
    case getType(scheduledDeliverablesActions.deleteDeliverable.success):
    case getType(scheduledDeliverablesActions.deleteDeliverable.failure): {
      return {
        ...state,
        deleteLoading: false,
      };
    }

    case getType(scheduledDeliverablesActions.deleteDeliverables.request):
      return {
        ...state,
        deleteLoading: true,
      };
    case getType(scheduledDeliverablesActions.deleteDeliverables.success):
    case getType(scheduledDeliverablesActions.deleteDeliverables.failure): {
      return {
        ...state,
        deleteLoading: false,
      };
    }

    case getType(
      scheduledDeliverablesActions.cloneScheduledDeliverable.request,
    ):
      return {
        ...state,
        cloneLoading: true,
      };
    case getType(
      scheduledDeliverablesActions.cloneScheduledDeliverable.success,
    ):
    case getType(
      scheduledDeliverablesActions.cloneScheduledDeliverable.failure,
    ): {
      return {
        ...state,
        cloneLoading: false,
      };
    }

    case getType(scheduledDeliverablesActions.sendClientEmails.request):
      return {
        ...state,
        emailSendLoading: true,
      };
    case getType(scheduledDeliverablesActions.sendClientEmails.success):
    case getType(scheduledDeliverablesActions.sendClientEmails.failure): {
      return {
        ...state,
        emailSendLoading: false,
      };
    }

    case getType(scheduledDeliverablesActions.deleteReportDownload.request):
      return {
        ...state,
        deleteReportLoading: true,
      };
    case getType(scheduledDeliverablesActions.deleteReportDownload.success):
    case getType(scheduledDeliverablesActions.deleteReportDownload.failure): {
      return {
        ...state,
        deleteReportLoading: false,
      };
    }

    case getType(
      scheduledDeliverablesActions.getScheduledDeliverableTransactions.request,
    ):
      return {
        ...state,
        transactions: {
          ...state.transactions,
          loading: true,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverableTransactions.success,
    ):
      return {
        ...state,
        transactions: {
          ...state.transactions,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverableTransactions.failure,
    ): {
      return {
        ...state,
        transactions: {
          ...state.transactions,
          loading: false,
        },
      };
    }

    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTransactions.request,
    ):
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTransactions.failure,
    ): {
      return {
        ...state,
      };
    }
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTransactions.success,
    ): {
      const newState = { ...state.scheduledDeliverablesTransactions };
      newState[action.payload.trade_id] = action.payload.data;

      return {
        ...state,
        scheduledDeliverablesTransactions: newState,
      };
    }

    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTestReports.request,
    ):
      return {
        ...state,
        testReports: {
          ...state.testReports,
          loading: true,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTestReports.success,
    ):
      return {
        ...state,
        testReports: {
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTestReports.failure,
    ): {
      return {
        ...state,
        testReports: {
          ...state.testReports,
          loading: false,
        },
      };
    }

    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTestReport.request,
    ):
      return {
        ...state,
        testReport: {
          ...state.testReport,
          loading: true,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTestReport.success,
    ):
      return {
        ...state,
        testReport: {
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(
      scheduledDeliverablesActions.getScheduledDeliverablesTestReport.failure,
    ): {
      return {
        ...state,
        testReport: {
          ...state.testReport,
          loading: false,
        },
      };
    }

    case getType(scheduledDeliverablesActions.rerunValidations.request):
      return {
        ...state,
        rerunValidationsLoading: true,
      };
    case getType(scheduledDeliverablesActions.rerunValidations.success):
    case getType(scheduledDeliverablesActions.rerunValidations.failure):
      return {
        ...state,
        rerunValidationsLoading: false,
      };

    default:
      return state;
  }
};

export default scheduledDeliverablesReducer;
